import { cn } from '@/lib/utils'

export interface IconProps {
  className?: string
}

/**
 * Shared geometry for the line icons: a 20-unit grid, currentColor stroke, and
 * decorative by default — every icon in this app sits beside its own text label.
 */
function StrokeIcon({
  className,
  strokeWidth = 1.8,
  children,
}: IconProps & { strokeWidth?: number; children: React.ReactNode }) {
  return (
    <svg
      className={cn('h-4 w-4', className)}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

/** Filled lock, the Platform's `.nav-lock-icon` glyph. */
export function LockIcon({ className, ...rest }: IconProps & { 'data-testid'?: string }) {
  return (
    <svg
      className={cn('h-4 w-4', className)}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      {...rest}
    >
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/** Filled clock, for a pending state. */
export function ClockIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-4 w-4', className)}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06l-2.78-2.78V5z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={cn('h-5 w-5', className)}>
      <path d="M7.5 4l6 6-6 6" />
    </StrokeIcon>
  )
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M5 7.5l5 5 5-5" />
    </StrokeIcon>
  )
}

export function ArrowLeftIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M8 4l-5 6 5 6M3 10h14" />
    </StrokeIcon>
  )
}

export function CheckIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={cn('h-3.5 w-3.5', className)} strokeWidth={2.5}>
      <path d="M4 10.5l4 4 8-9" />
    </StrokeIcon>
  )
}

export function DownloadIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M3.5 15.5h13" />
    </StrokeIcon>
  )
}

export function UsersIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className} strokeWidth={1.7}>
      <circle cx="7.5" cy="7" r="2.75" />
      <path d="M2.5 16c0-2.5 2.2-4.25 5-4.25S12.5 13.5 12.5 16" />
      <path d="M13.5 5.5a2.5 2.5 0 010 4.5M15 15.5c0-1.9-.9-3.3-2.3-4" />
    </StrokeIcon>
  )
}

export function FolderIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={cn('h-3.5 w-3.5', className)}>
      <path d="M2.5 6.5A1.5 1.5 0 014 5h3.2l1.4 2H16a1.5 1.5 0 011.5 1.5v6A1.5 1.5 0 0116 16H4a1.5 1.5 0 01-1.5-1.5z" />
    </StrokeIcon>
  )
}

export function LayoutIcon({ className }: IconProps) {
  return (
    <svg
      className={cn('h-3.5 w-3.5', className)}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M3 3h6v14H3zM11 3h6v6h-6zM11 11h6v6h-6z" />
    </svg>
  )
}

export function TrendUpIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M3 14l5-5 3 3 6-6M12 6h5v5" />
    </StrokeIcon>
  )
}

export function TrendDownIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M3 6l5 5 3-3 6 6M12 14h5V9" />
    </StrokeIcon>
  )
}

export function ListChecksIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M3 5l1.5 1.5L7 4M3 11l1.5 1.5L7 10M10 5.5h7M10 11.5h7M10 16h7M4 16h.01" />
    </StrokeIcon>
  )
}

export function BarChartIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <path d="M3 3v14h14M7 14v-3M10.5 14V7M14 14v-5" />
    </StrokeIcon>
  )
}

export function TargetIcon({ className }: IconProps) {
  return (
    <StrokeIcon className={className}>
      <circle cx="10" cy="10" r="7" />
      <circle cx="10" cy="10" r="3.5" />
      <circle cx="10" cy="10" r="0.75" fill="currentColor" />
    </StrokeIcon>
  )
}
